import { useState } from 'react';
import type { FormEvent } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { CorrectiveActionBadge, RiskBadge } from '@/components/StatusBadge';
import { shortDate, today } from '@/lib/format';
import { useSafetyInspectionActions, useSafetyInspections, useSiteVendors } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { SafetyCorrectiveAction, SafetyInspection, SafetyRiskLevel } from '@/lib/types';

const KINDS: SafetyInspection['inspectionKind'][] = ['정기', '수시', '합동', '특별'];
const RISKS: SafetyRiskLevel[] = ['중대', '주의', '경미'];

/**
 * 안전 점검 · 시정 지시.
 *
 * <p>점검은 회차로 돌고 지적은 회차에 매달린다 — 「이번 점검에서 몇 건이 남았나」가 현장이 세는
 * 단위다. 그래서 목록의 마디도 회차다.
 *
 * <p><b>지시한 쪽이 닫는다.</b> 협력사는 조치를 회신할 수 있을 뿐 스스로 「확인완료」로 올리지
 * 못한다 — 스스로 닫을 수 있으면 지적은 기록일 뿐 시정이 아니다. 지시하는 손은 감리와 직원이다(DOC-87 §3-3).
 */
export function SafetyInspectionsPage() {
  const { siteId, siteName, isJudge, isVendor } = useSite();
  const rounds = useSafetyInspections(siteId);
  const vendors = useSiteVendors(siteId);
  const { createRound, addFinding, respond, confirm } = useSafetyInspectionActions(siteId);

  const [openRound, setOpenRound] = useState(false);
  const [inspectedOn, setInspectedOn] = useState(today);
  const [inspectionKind, setInspectionKind] = useState<SafetyInspection['inspectionKind']>('정기');

  const [findingFor, setFindingFor] = useState<string | null>(null);
  const [finding, setFinding] = useState('');
  const [location, setLocation] = useState('');
  const [riskLevel, setRiskLevel] = useState<SafetyRiskLevel>('주의');
  const [dueDate, setDueDate] = useState(today);
  /** 수신 업체. 비면 지적이 직영분이 되어 어느 협력사 화면에도 뜨지 않는다 — 그래서 필수다. */
  const [vendorId, setVendorId] = useState('');

  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [actionNote, setActionNote] = useState('');

  const rows = rounds.data ?? [];
  const vendorRows = vendors.data ?? [];

  async function onCreateRound(event: FormEvent) {
    event.preventDefault();
    await createRound.mutateAsync({ inspectedOn, inspectionKind });
    setOpenRound(false);
  }

  async function onAddFinding(event: FormEvent, inspectionId: string) {
    event.preventDefault();
    await addFinding.mutateAsync({
      inspectionId,
      finding: finding.trim(),
      location: location.trim() === '' ? null : location.trim(),
      riskLevel,
      dueDate,
      vendorId,
    });
    setFindingFor(null);
    setFinding('');
    setLocation('');
    setVendorId('');
  }

  async function onRespond(event: FormEvent, actionId: string) {
    event.preventDefault();
    await respond.mutateAsync({ actionId, actionNote: actionNote.trim() });
    setRespondingTo(null);
    setActionNote('');
  }

  return (
    <>
      <PageHeader
        title="안전 점검"
        subtitle={siteName}
        action={
          isJudge && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpenRound(true)}>
              점검 등록
            </button>
          )
        }
      />

      {openRound && (
        <form onSubmit={onCreateRound} className="card mb-3">
          <div className="mb-4 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>점검일</span>
              <input type="date" className="input-field" value={inspectedOn} onChange={(e) => setInspectedOn(e.target.value)} required />
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>구분</span>
              <select
                className="input-field"
                value={inspectionKind}
                onChange={(e) => setInspectionKind(e.target.value as SafetyInspection['inspectionKind'])}
              >
                {KINDS.map((it) => <option key={it} value={it}>{it}</option>)}
              </select>
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={createRound.isPending}>
              {createRound.isPending ? '보내는 중입니다…' : '등록'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpenRound(false)}>취소</button>
          </div>
        </form>
      )}

      <QueryState
        pending={rounds.isPending}
        error={rounds.isError}
        empty={rows.length === 0}
        emptyText="등록된 점검이 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((round) => {
            const remaining = round.actions.filter((it) => it.status !== '확인완료');
            return (
              <li key={round.id} className="card">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-ui-md font-medium">
                      {round.roundNo}회차 · {round.inspectionKind}점검
                    </div>
                    <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                      {shortDate(round.inspectedOn)}
                      {round.inspector !== null && ` · ${round.inspector}`}
                    </div>
                  </div>
                  <span className={`badge shrink-0 ${remaining.length > 0 ? 'badge-red' : 'badge-gray'}`}>
                    지적 {round.actions.length}
                  </span>
                </div>

                <ul className="flex flex-col gap-2">
                  {round.actions.map((action) => (
                    <li
                      key={action.id}
                      className="rounded-md border p-3"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <div className="mb-1 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-ui-sm">{action.finding}</div>
                          <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                            {action.location ?? '위치 미지정'} · 기한 {shortDate(action.dueDate)}
                          </div>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <RiskBadge level={action.riskLevel} />
                          <CorrectiveActionBadge status={action.status} />
                        </div>
                      </div>

                      {isOverdue(action) && (
                        <div className="text-ui-2xs font-medium" style={{ color: 'var(--danger)' }}>
                          기한 초과
                        </div>
                      )}

                      {action.actionNote !== null && (
                        <p className="mt-2 rounded-md p-2 text-ui-sm" style={{ background: 'var(--bg-hover)' }}>
                          {action.actionNote}
                        </p>
                      )}

                      {isVendor && action.status === '지적' && (
                        respondingTo === action.id ? (
                          <form onSubmit={(e) => onRespond(e, action.id)} className="mt-3">
                            <textarea
                              className="input-field mb-2"
                              rows={3}
                              value={actionNote}
                              onChange={(e) => setActionNote(e.target.value)}
                              placeholder="조치 내용"
                              required
                            />
                            <div className="flex gap-2">
                              <button type="submit" className="btn-primary flex-1" disabled={respond.isPending || actionNote.trim() === ''}>
                                {respond.isPending ? '보내는 중입니다…' : '조치 회신'}
                              </button>
                              <button type="button" className="btn-secondary" onClick={() => setRespondingTo(null)}>취소</button>
                            </div>
                          </form>
                        ) : (
                          <button
                            type="button"
                            className="btn-secondary mt-3 w-full"
                            onClick={() => { setRespondingTo(action.id); setActionNote(''); }}
                          >
                            조치 회신
                          </button>
                        )
                      )}

                      {isJudge && action.status === '조치완료' && (
                        <button
                          type="button"
                          className="btn-primary mt-3 w-full"
                          onClick={() => confirm.mutate(action.id)}
                          disabled={confirm.isPending}
                        >
                          확인
                        </button>
                      )}

                      {action.confirmedOn !== null && (
                        <div className="mt-2 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                          {shortDate(action.confirmedOn)} {action.confirmedBy ?? ''} 확인
                        </div>
                      )}
                    </li>
                  ))}
                </ul>

                {isJudge && (
                  findingFor === round.id ? (
                    <form onSubmit={(e) => onAddFinding(e, round.id)} className="mt-3">
                      <label className="mb-2 block">
                        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>수신 업체</span>
                        <select className="input-field" value={vendorId} onChange={(e) => setVendorId(e.target.value)} required>
                          <option value="">선택</option>
                          {vendorRows.map((it) => (
                            <option key={it.id} value={it.id}>
                              {it.mainTrade === null ? it.name : `${it.name} · ${it.mainTrade}`}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="mb-2 block">
                        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>지적사항</span>
                        <textarea className="input-field" rows={2} value={finding} onChange={(e) => setFinding(e.target.value)} required />
                      </label>
                      <label className="mb-2 block">
                        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>위치</span>
                        <input type="text" className="input-field" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="B2 C구역" />
                      </label>
                      <div className="mb-3 flex gap-2">
                        <label className="flex-1">
                          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>위험도</span>
                          <select className="input-field" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value as SafetyRiskLevel)}>
                            {RISKS.map((it) => <option key={it} value={it}>{it}</option>)}
                          </select>
                        </label>
                        <label className="flex-1">
                          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>시정 기한</span>
                          <input type="date" className="input-field" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button type="submit" className="btn-primary flex-1" disabled={addFinding.isPending || vendorId === ''}>
                          {addFinding.isPending ? '보내는 중입니다…' : '지적 추가'}
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => setFindingFor(null)}>취소</button>
                      </div>
                    </form>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary mt-3 w-full"
                      onClick={() => { setFindingFor(round.id); setFinding(''); setLocation(''); setDueDate(today()); setVendorId(''); }}
                    >
                      지적 추가
                    </button>
                  )
                )}
              </li>
            );
          })}
        </ul>
      </QueryState>
    </>
  );
}

/** 기한을 넘겼는데 아직 손대지 않은 지적. 「중대」가 여기 걸리면 작업을 세워야 한다. */
function isOverdue(action: SafetyCorrectiveAction): boolean {
  return action.status === '지적' && action.dueDate < today();
}
